import { apiCallAsync } from 'src/redux/api/saga';
import {
  AI_PROMPT_STATUS_URL,
  AI_PROMPT_TEMPLATES_URL,
  AI_PROMPT_URL,
} from './constants';
import {
  AiPromptStatusResponse,
  AiPromptTemplate,
  AiPromptTemplateParams,
  AiSubmitPromptResponse,
} from './types';

interface ApiResponse {
  status?: number;
  data?: unknown;
}

// apiCallAsync resolves to undefined when the token is rejected.
function readResponse(response: ApiResponse, request: string): unknown {
  if (!response) {
    throw new Error(`${request}: no response received`);
  }

  const status = response.status ?? 0;
  if (status < 200 || status >= 300) {
    throw new Error(`${request}: request failed with status ${status}`);
  }

  return response.data;
}

// The list arrives wrapped: { prompt_templates: [...] }.
const TEMPLATE_LIST_FIELD = 'prompt_templates';

function asTemplateList(data: unknown): AiPromptTemplate[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (!data || typeof data !== 'object') {
    return [];
  }

  const list = (data as Record<string, unknown>)[TEMPLATE_LIST_FIELD];
  if (Array.isArray(list)) {
    return list;
  }

  // Handed over as-is, so the error downstream names its fields.
  return [data as Record<string, unknown>];
}

export async function fetchPromptTemplates(
  params: AiPromptTemplateParams,
): Promise<AiPromptTemplate[]> {
  const response = await apiCallAsync(AI_PROMPT_TEMPLATES_URL, params);
  return asTemplateList(readResponse(response, 'Prompt templates'));
}

export async function submitPrompt(
  promptText: string,
): Promise<AiSubmitPromptResponse> {
  const response = await apiCallAsync(
    AI_PROMPT_URL,
    { prompt_text: promptText },
    'post',
  );
  return readResponse(response, 'Prompt submission') as AiSubmitPromptResponse;
}

export async function fetchPromptStatus(
  eventId: number | string,
): Promise<AiPromptStatusResponse> {
  const response = await apiCallAsync(AI_PROMPT_STATUS_URL, {
    event_id: eventId,
  });
  return readResponse(response, 'Prompt status') as AiPromptStatusResponse;
}
